import { array, bool, object } from "prop-types";
import styled, { ThemeProvider } from "styled-components/macro";
import { rem } from "polished";

import { colors, fonts } from "../../../styles/Styles.variables";
import { RESTRICT_LISTINGS } from "../../../constants/url";
import { routes } from "../../../constants/routes";

import { NavLink } from "react-router-dom";

import PageSubMenu from "./Page.submenu";

export default function PageDesktopMenu({
  authenticated = false,
  location,
  settingsLinks,
  ...rest
}) {
  return (
    <ThemeProvider theme={authenticated ? themeAuth : themeAnon}>
      <Menu {...rest}>
        <li>
          <HomeLink />
        </li>
        {((RESTRICT_LISTINGS && authenticated) || !RESTRICT_LISTINGS) && (
          <li>
            <SubMenu
              id="submenu-toggle-listings"
              links={routes.results.categories}
              location={location}
            >
              {routes.results.title}
            </SubMenu>
          </li>
        )}
        {!authenticated && (
          <>
            <li>
              <SubMenu
                id="submenu-toggle-why"
                links={[routes.partners, routes.why]}
                location={location}
              >
                Why Holt
              </SubMenu>
            </li>
            <li>
              <NavLink exact to={routes.consignment.path}>
                Consign
              </NavLink>
            </li>
          </>
        )}
        {authenticated && (
          <li>
            <NavLink exact to={routes.compile.path}>
              {routes.compile.title}
            </NavLink>
          </li>
        )}
        <li>
          <NavLink to={routes.blog.path}>{routes.blog.title}</NavLink>
        </li>
        {authenticated && (
          <li>
            <SubMenu
              className="submenu-right"
              id="submenu-toggle-settings"
              links={settingsLinks}
              location={location}
              matchDepth="path"
            >
              My Settings
            </SubMenu>
          </li>
        )}
        {!authenticated && (
          <li>
            <NavLink exact to={routes.contact.path}>
              Contact
            </NavLink>
          </li>
        )}
      </Menu>
    </ThemeProvider>
  );
}

const themeAuth = {
  activeColor: colors.black,
  hoverColor: colors.gray600,
  linkColor: colors.black,
  outlineColor: colors.black,
};

const themeAnon = {
  activeColor: colors.yellow,
  hoverColor: colors.white,
  linkColor: colors.gray300,
  outlineColor: colors.gray300,
};

const Menu = styled.ul`
  align-items: center;
  display: flex;
  list-style: none;
  margin: 0 0 0 auto;

  li > a,
  .submenu-toggle {
    color: ${({ theme }) => theme.linkColor};
    display: block;
    font-size: ${rem("12px")};
    font-weight: ${fonts.weights.bold};
    line-height: 1;
    outline: ${rem("1px")} dotted transparent;
    padding-left: ${({ theme }) => rem(theme.spacing.x.sm + "px")};
    padding-right: ${({ theme }) => rem(theme.spacing.x.sm + "px")};
    text-decoration: none;
    text-transform: uppercase;
    transition: color 0.3s ease-in-out;
    white-space: nowrap;

    @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
      font-size: ${rem("16px")};
      padding-left: ${({ theme }) => rem(theme.spacing.x.md + "px")};
      padding-right: ${({ theme }) => rem(theme.spacing.x.md + "px")};
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
      padding-left: ${({ theme }) => rem(theme.spacing.x.lg + "px")};
      padding-right: ${({ theme }) => rem(theme.spacing.x.lg + "px")};
    }

    &:not(.home) {
      padding-bottom: ${rem("6px")};
      padding-top: ${rem("6px")};

      @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
        padding-bottom: ${rem("10px")};
        padding-top: ${rem("10px")};
      }
      @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
        padding-bottom: ${rem("20px")};
        padding-top: ${rem("20px")};
      }
    }

    &:focus {
      outline-color: ${({ theme }) => theme.outlineColor};
    }

    &:not(.active):hover {
      color: ${({ theme }) => theme.hoverColor};
    }

    &.active {
      color: ${({ theme }) => theme.activeColor};
    }
  }
`;

const SubMenu = styled(PageSubMenu)`
  .submenu-toggle {
    background: transparent;
    border: 0 none;
  }

  .submenu-list {
    background-color: ${colors.gray800};
    position: absolute;
    top: calc(100% + ${rem("10px")});
    padding: ${rem("20px")};
    min-width: ${rem("200px")};

    @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
      top: 100%;
      padding: ${rem("24px")};
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
      top: calc(100% - ${rem("5px")});
    }
  }

  &:not(.submenu-right) .submenu-list {
    left: ${rem("6px")};

    @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
      left: ${rem("10px")};
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
      left: ${rem("18px")};
    }
  }

  &.submenu-right .submenu-list {
    right: ${rem("6px")};

    @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
      right: ${rem("10px")};
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
      right: ${rem("18px")};
    }
  }

  .submenu-link {
    background-color: ${colors.gray800};
    font-size: ${rem("12px")};
    font-weight: ${fonts.weights.normal};
    line-height: 1;
    transition: color 0.2s ease-in-out;

    @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
      font-size: ${rem("16px")};
    }

    &:not(:last-child) {
      padding-bottom: ${rem("12px")};

      @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
        padding-bottom: ${rem("16px")};
      }
    }

    &:last-child {
      padding-bottom: 0px;
    }

    &:focus {
      outline: none;
    }

    &:not(.active) {
      color: ${colors.gray300};
    }

    &:not(.active):focus,
    &:not(.active):hover,
    &:not(.active):hover:focus {
      color: ${colors.white};
    }

    &.active {
      color: ${colors.yellow};
    }
  }
`;

const HomeLink = styled(({ className, ...props }) => (
  <NavLink
    {...props}
    className={`home ${className || ""}`}
    exact
    to={routes.home.path}
  >
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <title id="home-link">{routes.home.title}</title>
      <path d="M11.2632 0.252825C11.4739 0.0889646 11.7331 0 12 0C12.2669 0 12.5261 0.0889646 12.7368 0.252825L22.6104 7.9326C23.0434 8.26942 23.3937 8.70075 23.6345 9.19364C23.8754 9.68653 24.0004 10.2279 24 10.7765V21.6002C24 22.2367 23.7471 22.8471 23.2971 23.2972C22.847 23.7473 22.2365 24.0001 21.6 24.0001H16.8C16.1635 24.0001 15.553 23.7473 15.1029 23.2972C14.6529 22.8471 14.4 22.2367 14.4 21.6002V14.4004H9.6V21.6002C9.6 22.2367 9.34714 22.8471 8.89706 23.2972C8.44697 23.7473 7.83652 24.0001 7.2 24.0001H2.4C1.76348 24.0001 1.15303 23.7473 0.702944 23.2972C0.252856 22.8471 0 22.2367 0 21.6002V10.7765C0 9.66294 0.5136 8.61657 1.392 7.9326L11.2632 0.252825ZM12 2.71995L2.8632 9.82614C2.71854 9.93866 2.60158 10.0828 2.52129 10.2476C2.441 10.4123 2.39951 10.5932 2.4 10.7765V21.6002H7.2V14.4004C7.2 13.7639 7.45286 13.1535 7.90294 12.7034C8.35303 12.2533 8.96348 12.0005 9.6 12.0005H14.4C15.0365 12.0005 15.647 12.2533 16.0971 12.7034C16.5471 13.1535 16.8 13.7639 16.8 14.4004V21.6002H21.6V10.7765C21.6005 10.5932 21.559 10.4123 21.4787 10.2476C21.3984 10.0828 21.2815 9.93866 21.1368 9.82614L12 2.72235V2.71995Z" />
    </svg>
  </NavLink>
))`
  padding-bottom: ${rem("6px")};
  padding-top: ${rem("6px")};

  svg {
    display: block;
    height: ${rem("18px")};
    width: ${rem("18px")};

    @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
      height: ${rem("24px")};
      width: ${rem("24px")};
    }
  }

  path {
    fill: currentColor;
  }
`;

PageDesktopMenu.propTypes = {
  authenticated: bool,
  location: object,
  settingsLinks: array,
};
